import moment from 'moment-timezone';

export const isAValidDate = (value: string) => !isNaN(Number(value));

export const isValidDateMMDDYYYY = (dateString: string) => {
  const date = moment(dateString, 'MM/DD/YYYY');
  return date.isValid();
};

export const BMI_THRESHOLD = 25;

export const isEmailValid = (email: string) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);

export const isPhoneValid = (phone: string) => /^\(?\d{3}\)?-? *\d{3}-? *-?\d{4}$/.test(phone);

/** An inclusive range of plausible values for a measurement. */
export interface MeasurementRange {
  min: number;
  max: number;
}

/**
 * The plausible ranges for the intake measurements. The single source for both the validators
 * below and every message that states a range, so a changed bound cannot leave a validator and
 * its message disagreeing.
 */
export const HEIGHT_FEET_RANGE: MeasurementRange = { min: 1, max: 10 };
export const HEIGHT_INCHES_RANGE: MeasurementRange = { min: 0, max: 11 };
export const WEIGHT_LBS_RANGE: MeasurementRange = { min: 10, max: 999 };
export const BMI_RANGE: MeasurementRange = { min: 10, max: 100 };

const isWithin =
  ({ min, max }: MeasurementRange) =>
  (value: string) =>
    +value >= min && +value <= max;

export const isHeightFeetValid = isWithin(HEIGHT_FEET_RANGE);

export const isHeightInchesValid = isWithin(HEIGHT_INCHES_RANGE);

export const isWeightLbsValid = isWithin(WEIGHT_LBS_RANGE);

export const isBmiValid = isWithin(BMI_RANGE);

export const getBMI = ({
  weight,
  feet,
  inches,
}: {
  weight: number;
  feet: number;
  inches: number;
}) => {
  const height = feet * 12 + inches;
  return parseFloat(((weight * 703) / (height * height)).toFixed(2));
};

export const isLowBMI = (params: { weight: number; feet: number; inches: number }): boolean => {
  return getBMI(params) < BMI_THRESHOLD;
};

export const isValidDateOfBirth = (date: Date): { valid: boolean; reason?: string } => {
  if (!moment(date).isValid()) {
    return { valid: false, reason: 'Please enter a valid date of birth' };
  }
  if (moment(date).diff(moment(), 'years') > -16) {
    return {
      valid: false,
      reason: 'We are currently not accepting any members below the age of 16',
    };
  }
  return { valid: true };
};

export const isEmpty = (value: any) => {
  return value === null || value === '' || value === undefined;
};
